"use client";

import { Suspense, useEffect, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import { authClient } from "@/lib/auth-client";
import { safeRedirect } from "@/lib/auth-redirect";
import { track } from "@/lib/analytics";
import {
  clearPendingSignInMethod,
  markPendingSignInMethod,
} from "@/lib/pending-signin";
import { AuthShell } from "../components/AuthShell";
import { Button } from "@batch-and-bowl/ui/button";
import { Input } from "@batch-and-bowl/ui/input";
import { Label } from "@batch-and-bowl/ui/label";
import {
  AuthCard,
  AuthCardContent,
  AuthCardDescription,
  AuthCardHeader,
  AuthCardKicker,
  AuthCardTitle,
  AuthCardFooter,
} from "../components/AuthCard";
import { Loader2 } from "lucide-react";

import { BrandMark } from "../components/Brand";

function SignInForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  // Sanitized: an attacker-crafted ?redirect= must never leave this origin.
  const redirectTo = safeRedirect(searchParams.get("redirect"));

  // A brand-new Google user usually clicks "sign in", not "sign up" -- they
  // don't know yet that they have no account. The beta gate rejects them
  // during the OAuth callback, so this page needs the same error handling
  // /sign-up has. better-auth builds `?error=` from the thrown message with
  // spaces replaced by underscores, hence the underscored comparisons.
  const googleErrorParam = searchParams.get("googleError");
  const oauthError = searchParams.get("error");

  // The beta gate rejects the OAuth callback by redirecting back here with
  // ?googleError=1 (same tab), so the pending-flag write from the earlier
  // handleGoogleSignIn call never gets consumed by PostHogProvider. Clear it
  // here too, or a later email sign-in in this tab would double-fire
  // signed_in mislabeled as method:"google".
  useEffect(() => {
    if (googleErrorParam || oauthError) {
      clearPendingSignInMethod();
    }
  }, [googleErrorParam, oauthError]);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [needsVerification, setNeedsVerification] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendSent, setResendSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoading) {
      return;
    }

    setError("");
    setNeedsVerification(false);
    setResendSent(false);
    setIsLoading(true);

    try {
      // An email attempt supersedes any Google redirect abandoned via browser
      // Back. Clear before auth can resolve and the session analytics consumer
      // can mistake the stale hand-off for a successful Google sign-in.
      clearPendingSignInMethod();
      const result = await authClient.signIn.email({
        email,
        password,
      });

      if (result.error) {
        // Better Auth's "Invalid email or password" copy is already
        // non-enumerating (it doesn't distinguish an unknown email from a
        // wrong password), so we can surface it as-is.
        setError(result.error.message || "Invalid email or password");
        if (
          result.error.code === authClient.$ERROR_CODES.EMAIL_NOT_VERIFIED.code
        ) {
          setNeedsVerification(true);
        }
        return;
      }

      // Method enum only — never the email address (spec: no PII).
      track("signed_in", { method: "email" });

      router.push(redirectTo);
    } catch (err) {
      setError("An unexpected error occurred. Please try again.");
      console.error("Sign in error:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    if (isGoogleLoading) {
      return;
    }
    setIsGoogleLoading(true);
    try {
      // signIn.social navigates away, so an event fired here may never
      // flush. Hand off to PostHogProvider, which emits on the OAuth return.
      markPendingSignInMethod("google");
      await authClient.signIn.social({
        provider: "google",
        callbackURL: redirectTo,
        // Without this, a gate rejection during the OAuth callback redirects to
        // better-auth's built-in error page, which sanitizes any punctuated
        // message down to "UNKNOWN".
        errorCallbackURL: "/sign-in?googleError=1",
      });
    } catch (err) {
      // The redirect never happened, so the flag set above would otherwise
      // sit stale until a later email sign-in mislabels itself as Google.
      clearPendingSignInMethod();
      setError("Failed to sign in with Google");
      console.error("Google sign in error:", err);
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (isResending) {
      return;
    }

    setIsResending(true);

    try {
      await authClient.sendVerificationEmail({
        email,
        callbackURL: "/verify-email",
      });
    } catch (err) {
      console.error("Resend verification error:", err);
    } finally {
      // Non-enumerating by design: the endpoint always succeeds the same
      // way, so we show the same copy regardless of outcome.
      setResendSent(true);
      setIsResending(false);
    }
  };

  return (
    <AuthShell>
      <AuthCard>
        <AuthCardHeader>
          <div className="flex justify-center mb-4">
            <BrandMark size={56} />
          </div>
          <AuthCardKicker>Your account</AuthCardKicker>
          <AuthCardTitle>Welcome back</AuthCardTitle>
          <AuthCardDescription>
            Sign in to your Batch & Bowl account
          </AuthCardDescription>
        </AuthCardHeader>

        <AuthCardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div
                role="alert"
                className="p-3 text-sm text-destructive-text bg-destructive/10 rounded-md space-y-2"
              >
                <p>{error}</p>
                {needsVerification &&
                  (resendSent ? (
                    <p className="text-foreground">
                      Verification email sent. Check your inbox.
                    </p>
                  ) : (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleResendVerification}
                      disabled={isResending}
                    >
                      {isResending ? (
                        <>
                          <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        "Resend verification email"
                      )}
                    </Button>
                  ))}
              </div>
            )}

            {(googleErrorParam || oauthError) && (
              <p role="alert" className="text-sm text-destructive-text">
                {oauthError === "Sign_ups_are_invite_only"
                  ? // #823: the beta gate only fires when better-auth is about to
                    // CREATE a user, so on this page the rejection means the
                    // chosen Google identity matched no existing account. A
                    // returning user who picked the wrong Google account (or
                    // signed up with email) needs their original method, not
                    // an invite code.
                    "That Google account isn't linked to a Batch & Bowl account. If you already have one, sign in with your email and password, or choose the Google account you signed up with. New here? You need a valid invite code to sign up."
                  : oauthError === "The_beta_is_full"
                    ? "The beta is full right now. Please check back soon."
                    : "We could not complete that Google sign-in. Please try again."}
              </p>
            )}

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="email">Email</Label>
              </div>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isLoading}
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link
                  href="/forgot-password"
                  className="text-xs text-terracotta-700 dark:text-terracotta-300 hover:underline font-medium"
                >
                  Forgot password?
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={isLoading}
              />
            </div>

            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                "Sign in"
              )}
            </Button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-2 text-muted-foreground">
                Or continue with
              </span>
            </div>
          </div>

          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={handleGoogleSignIn}
            disabled={isLoading || isGoogleLoading}
          >
            <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
              <path
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                fill="#4285F4"
              />
              <path
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                fill="#34A853"
              />
              <path
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                fill="#FBBC05"
              />
              <path
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                fill="#EA4335"
              />
            </svg>
            Continue with Google
          </Button>
        </AuthCardContent>

        <AuthCardFooter className="flex justify-center">
          <p className="text-sm text-muted-foreground">
            Don&apos;t have an account?{" "}
            <Link
              href="/sign-up"
              className="text-terracotta-700 dark:text-terracotta-300 hover:underline font-medium"
            >
              Sign up
            </Link>
          </p>
        </AuthCardFooter>
      </AuthCard>
    </AuthShell>
  );
}

export default function SignInPage() {
  // useSearchParams() requires a Suspense boundary during static prerender —
  // without one, `next build` fails on this page.
  return (
    <Suspense
      fallback={
        <AuthShell>
          <div className="flex justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </AuthShell>
      }
    >
      <SignInForm />
    </Suspense>
  );
}
